/**
 * ContactsPage — adding contacts to CRM: who asks, what reaches CRM, where it stops.
 *
 * Every number on the page is counted by the server over ONE list of attempts (a request by
 * either door, with the card it drew), so the tiles, the funnel, the charts and the tables agree
 * with each other by construction; the page derives only shares, from two counts it prints.
 *
 * Two things the page says out loud, because the numbers cannot:
 *   1. WHERE THE DATA STARTS — the date comes from the API, never a copy here.
 *   2. WHOSE REQUESTS ARE COUNTED — admin accounts are dropped by username as well as by Slack id
 *      (a contacts row carries no Slack id), so the rule is the same one every other tab states.
 *
 * Filters come from the global FilterBar via DashboardLayout; no widget of its own. Every chart
 * carries the house hover tooltip (`ChartTooltip`, components/charts/ChartTooltip.tsx) with the
 * exact values for the point under the cursor.
 *
 * THE TIME SERIES follow the Engagement tab's Jira prefill timeline
 * (components/JiraActivityTimeline.tsx): what was offered as a filled area, what was taken as a
 * line on top, one axis, the global granularity, a totals badge in the header — and the Jira
 * conversion KPI beside it (pages/EngagementPage.tsx, «Jira conversion») becomes a line per period
 * here. A click on any period opens the attempts of that period.
 */

import * as React from 'react';
import {
  Area,
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { ChartTooltip, useChartTooltipTrigger, useIsTouchOnly } from '@/components/charts/ChartTooltip';
import { ContactsAttemptsModal } from '@/components/ContactsAttemptsModal';
import { Bars } from '@/components/charts/Bars';
import { Section } from '@/components/Section';
import { TimelineTotalsBadge, sumField } from '@/components/charts/TimelineTotalsBadge';
import { KPICard } from '@/components/KPICard';
import { RatingDistribution } from '@/components/RatingDistribution';
import { RecountNotice } from '@/components/RecountNotice';
import { SortableTable, type SortableColumn } from '@/components/SortableTable';
import { TabErrorBoundary } from '@/components/TabErrorBoundary';
import { Card, CardContent } from '@/components/ui/card';
import { RoleBadge } from '@/components/ui/role-badge';
import { UserLabel } from '@/components/ui/user-label';
import { useContactsKpis, type ContactsSlice } from '@/hooks/use-contacts';
import { useUrlFilters } from '@/hooks/use-url-filters';
import { chartPalette } from '@/lib/chart-palette';
import { formatBucketLabel } from '@/lib/format-bucket';
import { formatDateTime } from '@/lib/format-date';
import type {
  ContactsSourceRow,
  ContactsStep,
  ContactsTrendPoint,
  ContactsUserRow,
  RatingBucket,
} from '@/services/api-types';

/** A percentage for a chart point; null (a gap in the line) when there is nothing to divide by. */
export const pct = (n: number, d: number): number | null => (d > 0 ? Math.round((n / d) * 1000) / 10 : null);

/** A share derived from the two counts printed beside it; nothing to divide by is an em-dash. */
export function share(n: number | undefined, d: number | undefined): string {
  if (!d) return '—';
  return `${Math.round(((n ?? 0) / d) * 100)}%`;
}

const STEP_LABEL: Record<ContactsStep, string> = {
  request: 'Requests',
  card: 'Card shown',
  fix: '«Поправить» opened',
  sent: '«Добавить» pressed',
  imported: 'Imported',
  rated: 'Rated',
  cancelled: 'Cancelled',
  abandoned: 'Abandoned (no send, no cancel)',
};

/** The request outcomes the two doors write (contacts_entrance / contacts_tool). Unknown words fall
 *  through as they are: a new refusal should show up named, not vanish into «other». */
const OUTCOME_LABEL: Record<string, string> = {
  card: 'Card drawn',
  loginGone: 'CRM login missing / expired',
  noAddContact: 'No right to add contacts',
  notOpenYet: 'Not open to him yet',
  caps: 'Over a cap',
  nothingFound: 'No contact found',
  couldNotRead: 'Could not read',
  crmUnread: 'CRM did not answer',
  permsUnread: 'Rights not readable',
  historyUnread: 'History not readable',
  notSaved: 'Draft not saved',
  ticketUnread: 'Ticket not readable',
  notUnderAs: 'Not allowed under /as',
  onlyInADm: 'Outside a DM',
  alreadyOffered: 'Already a card this turn',
  notAvailable: 'Feature off',
  notHisLogin: 'Service login',
  stopped: 'Stopped',
  threw: 'Draft failed with an error',
  unknown: 'Not recorded (tool, before 2026-09-29)',
};
export const outcomeLabel = (o: string): string => OUTCOME_LABEL[o] ?? o;

const SOURCE_LABEL: Record<string, string> = {
  text: 'Typed text',
  jira: 'Jira ticket',
  file: 'File (kind not recorded)',
  'file:sheet': 'Spreadsheet',
  'file:shot': 'Screenshot / document',
};
export const sourceLabel = (s: string): string => SOURCE_LABEL[s] ?? s;

/** The row checks' reasons (services/contacts/check.ts `FixReason['why']`) — each is a distinct check,
 *  none an older spelling of another: `noEmail` is an address that is absent, `email` one that is there
 *  but not an address; the same pair for the phone. An unknown key is shown as it is. */
const FIX_LABEL: Record<string, string> = {
  missing: 'Required field empty (not e-mail / phone)',
  noEmail: 'No e-mail',
  email: 'E-mail not an address',
  noPhone: 'No phone',
  phone: 'Phone not readable as a number',
  company: 'Corporate with no company name',
  tooLong: 'A value too long',
  type: 'Type not Individual / Corporate',
  manager: 'Manager he may not assign',
  supportedBy: 'Supported by: unknown person',
  partner: 'Partner e-mail not an address',
  event: 'Event not in CRM\'s list',
  channel: 'Channel not in CRM\'s list',
  advert: 'Advert not in CRM\'s list',
};
export const fixLabel = (r: string): string => FIX_LABEL[r] ?? r;

const REFUSAL_LABEL = { duplicate: 'Already in CRM', validation: 'Rejected by CRM', other: 'Whole file refused' } as const;

function formatSeconds(sec: number | null | undefined): string | null {
  if (sec == null) return null;
  return sec < 90 ? `${Math.round(sec)} s` : `${(sec / 60).toFixed(1)} min`;
}


const USER_COLUMNS: Array<SortableColumn<ContactsUserRow>> = [
  { key: 'realName', header: 'Name', format: (_v, r) => <UserLabel name={r.realName} username={r.userId} variant="cell" /> },
  { key: 'userId', header: 'User', format: (_v, r) => (r.userId ? `@${r.userId}` : '—') },
  { key: 'role', header: 'Role', format: (_v, r) => <RoleBadge role={r.role} /> },
  { key: 'requests', header: 'Requests', align: 'right' },
  { key: 'imports', header: 'Imports', align: 'right' },
  { key: 'created', header: 'Created', align: 'right' },
  { key: 'refused', header: 'Refused', align: 'right' },
  {
    key: 'meanRating',
    header: 'Avg rating',
    align: 'right',
    format: (_v, r) => (r.meanRating == null ? '—' : `${r.meanRating.toFixed(1)} (${r.ratings})`),
  },
  { key: 'lastUsed', header: 'Last used', format: (_v, r) => formatDateTime(r.lastUsed) },
];

/** The two success rates, derived here from the counts in the same row. */
type SourceRow = ContactsSourceRow & { cardToImport: string; accepted: string };
const withRates = (r: ContactsSourceRow): SourceRow => ({
  ...r, cardToImport: share(r.imported, r.cards), accepted: share(r.created, r.created + r.refused),
});

const SOURCE_COLUMNS: Array<SortableColumn<SourceRow>> = [
  { key: 'source', header: 'Source', format: (_v, r) => sourceLabel(r.source) },
  { key: 'cards', header: 'Cards', align: 'right' },
  { key: 'sent', header: 'Sent', align: 'right' },
  { key: 'imported', header: 'Imported', align: 'right' },
  { key: 'cardToImport', header: 'Card → import', align: 'right', sortable: false },
  { key: 'created', header: 'Created', align: 'right' },
  { key: 'refused', header: 'Refused', align: 'right' },
  { key: 'accepted', header: 'Rows accepted', align: 'right', sortable: false },
];

export function ContactsPage(): React.ReactElement {
  const { filters } = useUrlFilters();
  const contacts = useContactsKpis(filters);
  const { data, isLoading, isError, refetch } = contacts;
  const granularity = data?.granularity ?? 'daily';
  // The drill-down: which slice a click opened, and the title it reads under.
  const [drill, setDrill] = React.useState<{ slice: ContactsSlice; title: string } | null>(null);
  const openDrill = (slice: ContactsSlice, title: string) => setDrill({ slice, title });
  const pickPeriod = (bucket: string) => openDrill({ kind: 'day', value: bucket }, formatBucketLabel(bucket, granularity));
  const trend = data?.trend ?? [];
  // Shares per period, each from the two counts the tooltip prints beside it; no denominator → a gap.
  const conversion = trend.map((t) => ({
    ...t,
    requestToCard: pct(t.requestsWithCard, t.requests),
    cardToSend: pct(t.sends, t.cards),
    sendToCreated: pct(t.created, t.rowsSent),
  }));

  const adminsIncluded = filters.admins === 'included';
  const funnel = data
    ? (['request', 'card', 'fix', 'sent', 'imported', 'rated', 'cancelled', 'abandoned'] as ContactsStep[]).map((step) => ({
        step,
        count: data.funnel[step],
      }))
    : [];
  const refusals = data
    ? (Object.keys(REFUSAL_LABEL) as Array<keyof typeof REFUSAL_LABEL>).map((kind) => ({ kind, count: data.refusals[kind] }))
    : [];

  if (isError) {
    return (
      <Card>
        <CardContent className="p-4 space-y-2">
          <div className="text-sm font-medium text-red-600">Contacts failed to load</div>
          <button type="button" onClick={() => void refetch()} className="text-xs underline text-primary hover:no-underline">
            Retry
          </button>
        </CardContent>
      </Card>
    );
  }

  return (
    <TabErrorBoundary name="Contacts">
      <section aria-labelledby="contacts-heading" className="space-y-4">
        <header>
          <h1 id="contacts-heading" className="text-2xl font-semibold">Contacts</h1>
          <p className="text-sm text-muted-foreground" data-testid="contacts-data-from">
            {data ? (
              <>Data from {data.dataFrom}. Adding contacts to CRM was not recorded before this date.</>
            ) : (
              <span className="inline-block h-4 w-80 max-w-full rounded bg-muted animate-pulse align-middle" />
            )}
          </p>
          <p className="text-sm text-muted-foreground" data-testid="contacts-admin-note">
            {adminsIncluded
              ? 'Requests from admin accounts are included, here as on every other tab.'
              : 'Requests from admin accounts are excluded, here as on every other tab.'}
          </p>
          <RecountNotice queries={[contacts]} />
        </header>

        <div className="grid gap-3 grid-cols-2 lg:grid-cols-4 xl:grid-cols-7">
          <KPICard label="Users" value={data?.users ?? null} format="number" loading={isLoading} showDelta={false}
            subLabel={data ? `${data.newUsers} new` : undefined}
            tooltip="People who asked to add contacts in the period. New = their first request ever falls in it." />
          <KPICard label="Requests" value={data?.requests ?? null} format="number" loading={isLoading} showDelta={false} />
          <KPICard label="Cards shown" value={data?.cards ?? null} format="number" loading={isLoading} showDelta={false}
            subLabel={data ? `${share(data.cards, data.requests)} of requests` : undefined} />
          <KPICard label="«Добавить» presses" value={data?.sends ?? null} format="number" loading={isLoading} showDelta={false}
            subLabel={data ? `${share(data.sends, data.cards)} of cards` : undefined} />
          <KPICard label="Contacts created" value={data?.created ?? null} format="number" loading={isLoading} showDelta={false}
            subLabel={data ? `in ${data.imports} imports` : undefined} />
          <KPICard label="Refused by CRM" value={data?.refused ?? null} format="number" loading={isLoading} showDelta={false}
            subLabel={data ? `${share(data.refused, data.created + data.refused)} of rows sent` : undefined} />
          <KPICard label="Avg rating" value={data?.meanRating ?? null} format="rating" loading={isLoading} showDelta={false}
            subLabel={data ? `${data.ratings} rating${data.ratings === 1 ? '' : 's'}` : undefined} />
        </div>

        <div className="grid gap-4 lg:grid-cols-2">
          <Section title="Funnel" testId="contacts-funnel" hint>
            <Bars
              testId="contacts-funnel-chart"
              data={funnel}
              categoryKey="step"
              categoryLabel={(s) => STEP_LABEL[s as ContactsStep] ?? s}
              series={[{ key: 'count', name: 'Attempts', color: chartPalette.primary }]}
              vertical
              height={280}
              onPick={(r) => openDrill({ kind: 'step', value: r.step }, STEP_LABEL[r.step])}
              valueFormatter={(v, name) => `${name}: ${v} · ${share(Number(v), data?.funnel.request)} of requests`}
            />
          </Section>

          <Section title="Requests, cards and «Добавить» presses" testId="contacts-trend-volume" hint
            badge={<TimelineTotalsBadge loading={isLoading} items={[
              { key: 'requests', label: 'Requests', color: chartPalette.companion, value: sumField(trend, 'requests') },
              { key: 'cards', label: 'Cards', color: chartPalette.primary, value: sumField(trend, 'cards') },
              { key: 'sends', label: 'Presses', color: chartPalette.positiveSoft, value: sumField(trend, 'sends') },
            ]} />}>
            <TimeChart testId="contacts-trend-volume-chart" data={trend} granularity={granularity} onPick={pickPeriod}
              series={[
                { key: 'requests', name: 'Requests', color: chartPalette.companion, kind: 'area' },
                { key: 'cards', name: 'Cards shown', color: chartPalette.primary, kind: 'line' },
                { key: 'sends', name: '«Добавить» presses', color: chartPalette.positiveSoft, kind: 'line' },
                { key: 'imports', name: 'CRM answered', color: chartPalette.muted, kind: 'line', dashed: true },
              ]} />
          </Section>
        </div>

        <div className="grid gap-4 lg:grid-cols-2">
          <Section title="Contacts created vs refused by CRM" testId="contacts-trend-outcome" hint
            badge={<TimelineTotalsBadge loading={isLoading} items={[
              { key: 'created', label: 'Created', color: chartPalette.positive, value: sumField(trend, 'created') },
              { key: 'refused', label: 'Refused', color: chartPalette.negative, value: sumField(trend, 'refused') },
            ]} />}>
            <TimeChart testId="contacts-trend-outcome-chart" data={trend} granularity={granularity} onPick={pickPeriod}
              series={[
                { key: 'created', name: 'Created', color: chartPalette.positive, kind: 'bar', stackId: 'created' },
                { key: 'refusedDuplicate', name: REFUSAL_LABEL.duplicate, color: chartPalette.warning, kind: 'bar', stackId: 'refused' },
                { key: 'refusedValidation', name: REFUSAL_LABEL.validation, color: chartPalette.negative, kind: 'bar', stackId: 'refused' },
                { key: 'refusedOther', name: REFUSAL_LABEL.other, color: chartPalette.muted, kind: 'bar', stackId: 'refused' },
              ]} />
          </Section>

          <Section title="Conversion" testId="contacts-trend-conversion" hint>
            <TimeChart testId="contacts-trend-conversion-chart" data={conversion} granularity={granularity} onPick={pickPeriod}
              percent
              series={[
                { key: 'requestToCard', name: 'Request → card', color: chartPalette.companion, kind: 'line', of: ['requestsWithCard', 'requests'] },
                { key: 'cardToSend', name: 'Card → «Добавить»', color: chartPalette.primary, kind: 'line', of: ['sends', 'cards'] },
                { key: 'sendToCreated', name: '«Добавить» → created rows', color: chartPalette.positive, kind: 'line', of: ['created', 'rowsSent'] },
              ]} />
          </Section>
        </div>

        <div className="grid gap-4 lg:grid-cols-2">
          <Section title="Users: active and new" testId="contacts-trend-users" hint>
            <TimeChart testId="contacts-trend-users-chart" data={trend} granularity={granularity} onPick={pickPeriod}
              series={[
                { key: 'newUsers', name: 'New users', color: chartPalette.positiveSoft, kind: 'bar' },
                { key: 'users', name: 'Active users', color: chartPalette.activeUsers, kind: 'line' },
              ]} />
          </Section>

          <Section title="Time saved by contacts" testId="contacts-trend-time" hint
            badge={<TimelineTotalsBadge loading={isLoading} items={[
              { key: 'time', label: 'Minutes', color: chartPalette.positive, value: data?.timeSavedMinutes ?? 0 },
            ]} />}>
            <p className="text-xs text-muted-foreground">
              The Overview rule: 2 min per contact created, 1 per contact CRM already had, a spreadsheet batch at most 30.
            </p>
            <TimeChart testId="contacts-trend-time-chart" data={trend} granularity={granularity} onPick={pickPeriod}
              series={[{ key: 'timeSavedMinutes', name: 'Minutes saved', color: chartPalette.positive, kind: 'bar' }]} />
          </Section>
        </div>

        <Section title="Rating and speed" testId="contacts-trend-quality" hint>
          <TimeChart testId="contacts-trend-quality-chart" data={trend} granularity={granularity} onPick={pickPeriod}
            rightDomain={[1, 5]}
            series={[
              { key: 'medianCardToSendSec', name: 'Card → «Добавить», median s', color: chartPalette.primary, kind: 'line' },
              { key: 'medianSendToCrmSec', name: '«Добавить» → CRM, median s', color: chartPalette.muted, kind: 'line' },
              { key: 'meanRating', name: 'Average rating', color: chartPalette.rating[5], kind: 'line', axis: 'right', dashed: true },
            ]} />
        </Section>

        <Section title="Who uses it" testId="contacts-users">
          <SortableTable
            tableId="contacts-users"
            rows={data?.byUser ?? []}
            columns={USER_COLUMNS}
            getRowKey={(r) => r.userId ?? `(unknown-${(data?.byUser ?? []).indexOf(r)})`}
            // No drill for the anonymous row: there is nobody to name in the title or to filter by.
            onRowClick={(r) => { if (r.userId) openDrill({ kind: 'user', value: r.userId }, r.realName ?? `@${r.userId}`); }}
            isLoading={isLoading}
            emptyMessage="No requests in selected period"
          />
        </Section>

        <div className="grid gap-4 lg:grid-cols-2">
          <Section title="By source" testId="contacts-sources">
            <SortableTable
              tableId="contacts-sources"
              rows={(data?.bySource ?? []).map(withRates)}
              columns={SOURCE_COLUMNS}
              getRowKey={(r) => r.source}
              onRowClick={(r) => openDrill({ kind: 'source', value: r.source }, sourceLabel(r.source))}
              isLoading={isLoading}
              emptyMessage="No cards in selected period"
            />
          </Section>

          <Section title="Why no card" testId="contacts-outcomes" hint>
            <Bars
              testId="contacts-outcomes-chart"
              data={(data?.requestOutcomes ?? []).map((o) => ({ ...o }))}
              categoryKey="outcome"
              categoryLabel={outcomeLabel}
              series={[{ key: 'count', name: 'Requests', color: chartPalette.muted }]}
              onPick={(r) => openDrill({ kind: 'outcome', value: r.outcome }, outcomeLabel(r.outcome))}
              vertical
              height={Math.max(160, (data?.requestOutcomes.length ?? 0) * 30)}
            />
          </Section>
        </div>

        <div className="grid gap-4 lg:grid-cols-2">
          <Section title="CRM refusals" testId="contacts-refusals" hint>
            <Bars
              testId="contacts-refusals-chart"
              data={refusals}
              categoryKey="kind"
              categoryLabel={(k) => REFUSAL_LABEL[k as keyof typeof REFUSAL_LABEL] ?? k}
              series={[{ key: 'count', name: 'Rows refused', color: chartPalette.negative }]}
              onPick={(r) => openDrill({ kind: 'refusal', value: r.kind }, REFUSAL_LABEL[r.kind])}
              height={200}
            />
          </Section>
          <Section title="Why rows needed a fix" testId="contacts-fix-reasons" hint>
            <Bars
              testId="contacts-fix-chart"
              data={(data?.fixReasons ?? []).slice(0, 10).map((f) => ({ ...f }))}
              categoryKey="reason"
              categoryLabel={fixLabel}
              series={[{ key: 'rows', name: 'Rows', color: chartPalette.warning }]}
              onPick={(r) => openDrill({ kind: 'fixReason', value: r.reason }, `Needed a fix: ${fixLabel(r.reason)}`)}
              vertical
              height={Math.max(160, Math.min(10, data?.fixReasons.length ?? 0) * 30)}
            />
          </Section>
        </div>

        <div className="grid gap-4 lg:grid-cols-2">
          <Section title="Ratings" testId="contacts-ratings" hint>
            <RatingDistribution
              buckets={(data?.ratingBuckets ?? []) as RatingBucket[]}
              loading={isLoading}
              onBarClick={(n) => openDrill({ kind: 'rating', value: String(n) }, `Rated ${n}★`)}
            />
          </Section>
          <Section title="Speed" testId="contacts-speed">
            <div className="grid grid-cols-2 gap-3">
              <KPICard label="Card → «Добавить» (median)" value={formatSeconds(data?.speed.medianCardToSendSec)} loading={isLoading}
                showDelta={false} subLabel={data ? `over ${data.speed.sends} sends` : undefined} />
              <KPICard label="«Добавить» → CRM (median)"
                value={formatSeconds(data?.speed.medianSendToCrmMs == null ? null : data.speed.medianSendToCrmMs / 1000)}
                loading={isLoading} showDelta={false} subLabel={data ? `over ${data.speed.timedImports} imports` : undefined} />
            </div>
          </Section>
        </div>

        <Section title="Feedback" testId="contacts-feedback">
          {data && data.feedback.length === 0 ? (
            <p className="text-sm text-muted-foreground">No written feedback in selected period.</p>
          ) : (
            <ul className="space-y-2" data-testid="contacts-feedback-list">
              {(data?.feedback ?? []).map((f) => (
                <li key={`${f.traceId}-${f.at}`} className="text-sm">
                  <div className="text-xs text-muted-foreground">
                    <UserLabel name={f.realName} username={f.userId} variant="cell" /> · {formatDateTime(f.at)}
                    {f.rating != null && ` · ${f.rating}★`}
                  </div>
                  <div className="whitespace-pre-wrap">{f.text}</div>
                </li>
              ))}
            </ul>
          )}
        </Section>
      </section>
      <ContactsAttemptsModal
        open={drill !== null}
        onClose={() => setDrill(null)}
        filters={filters}
        slice={drill?.slice ?? { kind: 'all' }}
        title={drill?.title ?? ''}
        outcomeLabel={outcomeLabel}
      />
    </TabErrorBoundary>
  );
}

type TrendKey = keyof ContactsTrendPoint & string;

interface TimeSeries {
  key: string;
  name: string;
  color: string;
  kind: 'bar' | 'line' | 'area';
  stackId?: string;
  /** The right axis (the rating scale); default left. */
  axis?: 'right';
  dashed?: boolean;
  /** For a percentage line: the two counts it is made of, printed in the tooltip beside it. */
  of?: [TrendKey, TrendKey];
}

/**
 * One period chart: the global granularity on X, bars / lines / an area on one axis (a second,
 * right-hand axis only for the rating), the house tooltip carrying every value of the hovered
 * period — a share with the two counts it came from — and a click on the period opening its
 * attempts. Series are mapped straight into the chart's children, never a fragment (Recharts 2
 * does not look inside one — see `Bars`).
 */
function TimeChart<T extends { bucket: string }>({
  data,
  granularity,
  series,
  onPick,
  testId,
  percent = false,
  rightDomain,
  height = 260,
}: {
  data: T[];
  granularity: 'hourly' | 'daily' | 'weekly';
  series: TimeSeries[];
  onPick?: (bucket: string) => void;
  testId: string;
  percent?: boolean;
  rightDomain?: [number, number];
  height?: number;
}): React.ReactElement {
  const tooltipTrigger = useChartTooltipTrigger();
  const isTouchOnly = useIsTouchOnly();
  const value = (r: T, k: string): unknown => (r as Record<string, unknown>)[k];
  // A share of 0% is a point; only a share over nothing (null) is blank. A count of 0 is blank.
  const blank = (v: unknown) => (percent ? v == null : !v);
  if (data.length === 0 || data.every((r) => series.every((s) => blank(value(r, s.key))))) {
    return (
      <div data-testid={`${testId}-empty`} className="flex items-center justify-center text-sm text-muted-foreground" style={{ height }}>
        Nothing in selected period
      </div>
    );
  }
  const bucketLabel = (v: unknown) => (typeof v === 'string' ? formatBucketLabel(v, granularity) : String(v ?? ''));
  const hasRight = series.some((s) => s.axis === 'right');
  const format = (row: T | undefined) => (v: unknown, name: string | undefined): React.ReactNode => {
    const s = series.find((x) => x.name === name);
    if (v === null || v === undefined) return `${name}: —`;
    if (s?.of && row) return `${name}: ${v}% (${value(row, s.of[0])} of ${value(row, s.of[1])})`;
    if (typeof v === 'number' && !Number.isInteger(v)) return `${name}: ${v.toFixed(1)}`;
    return `${name}: ${String(v)}`;
  };
  return (
    <div data-testid={testId} style={{ width: '100%', height }}>
      <ResponsiveContainer width="100%" height={height}>
        <ComposedChart
          data={data}
          margin={{ top: 8, right: hasRight ? 8 : 24, left: 0, bottom: 0 }}
          onClick={
            onPick
              ? (state: unknown) => {
                  const label = (state as { activeLabel?: unknown } | null)?.activeLabel;
                  if (typeof label === 'string' && label) onPick(label);
                }
              : undefined
          }
          style={onPick ? { cursor: 'pointer' } : undefined}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="bucket" tickFormatter={(v: string) => formatBucketLabel(v, granularity)} />
          <YAxis yAxisId="left" allowDecimals={!percent ? false : true} domain={percent ? [0, 100] : undefined}
            tickFormatter={percent ? (v: number) => `${v}%` : undefined} />
          {hasRight && <YAxis yAxisId="right" orientation="right" domain={rightDomain} allowDecimals={false} />}
          {!(isTouchOnly && onPick) && (
            <Tooltip
              {...tooltipTrigger}
              cursor={false}
              content={(props) => (
                <ChartTooltip
                  {...(props as React.ComponentProps<typeof ChartTooltip>)}
                  labelFormatter={bucketLabel}
                  valueFormatter={format((props.payload?.[0]?.payload as T | undefined))}
                />
              )}
            />
          )}
          <Legend />
          {series.map((s) =>
            s.kind === 'bar' ? (
              <Bar key={s.key} yAxisId={s.axis ?? 'left'} dataKey={s.key} name={s.name} fill={s.color} stackId={s.stackId}
                isAnimationActive={false} />
            ) : s.kind === 'area' ? (
              <Area key={s.key} yAxisId={s.axis ?? 'left'} type="monotone" dataKey={s.key} name={s.name} stroke={s.color}
                fill={s.color} fillOpacity={0.18} isAnimationActive={false} />
            ) : (
              <Line key={s.key} yAxisId={s.axis ?? 'left'} type="monotone" dataKey={s.key} name={s.name} stroke={s.color}
                strokeWidth={2} strokeDasharray={s.dashed ? '4 3' : undefined} dot={{ r: 2 }} connectNulls
                isAnimationActive={false} />
            ),
          )}
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}
